/** Client-facing and staff-facing copy, kept out of components so tone stays consistent. */

export const AGENCY = {
  name: 'Sonder Media House',
  email: 'hello@sondermediahouse.com',
  phone: '+1.513.444.6113',
  site: 'https://sondermediahouse.com',
} as const

export const WELCOME = {
  eyebrow: 'Your client portal',
  headline: (company: string) => `Hello, ${company}.`,
  subhead: 'Welcome to Sonder Media House.',
  /** {names}: first names of the people we met; {brands}: brand list. */
  lead: (names: string, brands: string) =>
    `${names}, it was a pleasure meeting you. From our conversations we've already started the profiles for ${brands}. This is where we confirm the details, connect your accounts, and gather what we need before the first shoot. It saves as you go, and anyone on your team can pick it up.`,
  signedInAs: 'Signed in as',
  signedInHint: 'Your answers are saved under your name, so your team can see who filled in what.',
  timeTitle: 'What to expect',
  duration: 'About ten minutes. Anything you skip, we cover together.',
  scopeTitle: 'About this engagement',
  begin: 'Begin',
} as const

export const TEAM_SCREEN = {
  eyebrow: 'Client onboarding',
  title: 'Say hello to your Creative Powerhouse.',
  body: 'Reach out to any of us, during onboarding or any time after.',
  cta: 'Start onboarding',
} as const

/** The client's home page, which every visit after the introduction opens on. */
export const HOME = {
  eyebrow: 'Home',
  title: (firstName: string | null) => (firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'),
  inProgress: (pct: number) => `Onboarding is ${pct}% done. Pick up where you left off; everything saves as you go.`,
  notStarted: 'Onboarding takes about ten minutes. Anything you skip, we cover together.',
  submitted: (date: string) => `Onboarding submitted ${date}. You can keep editing anything; changes save as you go.`,
  continue: (step: string) => `Continue: ${step}`,
  review: 'Review & submit',
  stepsTitle: 'Onboarding steps',
  teamTitle: 'Your Sonder team',
  teamLink: 'Meet the team',
  peopleTitle: 'Your people',
  people: (n: number) => (n === 1 ? 'One person can sign in.' : `${n} people can sign in.`),
  peopleLink: 'Invite a teammate',
  galleriesTitle: 'Galleries',
  galleriesBody: 'Photos and video from each shoot, ready to download, arrive after the first session.',
  galleriesLink: 'Open galleries',
  introTitle: 'The introduction',
  introBody: 'The welcome and your team, as you saw them on your first visit.',
  introLink: 'Go through it again',
} as const

export const RAIL = {
  helpTitle: 'Need a hand?',
  helpBody: 'Loop in your marketing director or GM. Invite them and they can fill in their part; everything saves to one profile.',
  invite: 'Invite a teammate',
} as const

export const STAFF = {
  prepBanner: 'Fill in what you know. The client confirms it.',
  previewBanner: 'Previewing as a client: nothing is saved. Try anything.',
  /** Labels the prefill number, which counts answers filled in, not the client's confirmations. */
  prepFilledLabel: 'Filled in',
  /** The row under it: answers the client has tapped Looks right on. */
  prepConfirmedLabel: 'Confirmed by the client',
  prepProgressNote: 'Everything the team can fill in. Granting platform access is the client’s job, so it isn’t counted here.',
  /** Replaces the client's "Need a hand?" panel while staff prefill. */
  prepRailTitle: 'Ready for the client?',
  prepRailBody: 'Once what you know is in, invite the client. They review each answer and tap Looks right, and their progress starts there.',
  prepRailPreview: 'Preview as the client',
  prepRailInvite: 'Invite the client',
} as const

export const REVIEW = {
  title: 'Almost there.',
  body: 'Skim the summary and go back to any section. Submit when it looks right; you can keep editing after.',
  submit: 'Submit onboarding',
  resubmit: 'Re-submit',
  doneTitle: 'Submitted. Thank you.',
  doneBody: 'We have your profile and will follow up with next steps. You can still edit anything; changes save automatically.',
} as const

export const PASSWORD_NOTICE =
  'Prefer granting access from your side wherever a platform allows it. Anything you type here is encrypted before it is stored, and only the Sonder team can open it.'

export const META_PARTNER_STEPS = (businessId: string) =>
  `In Meta Business Suite: Settings → Partners → Add → enter Business ID ${businessId} (Sonder Media House) and grant your Page and Instagram account. No Business Portfolio? Add Hunter Reynolds as a Page admin instead.`
